/**
 * buildCierreShareCaption.ts — Caption corto y NO editable* para compartir el cierre.
 *
 * QUÉ HACE:
 *  - Devuelve un texto breve para acompañar la imagen al compartir un cierre de caja.
 *  - Formato: "Cierre de Caja • DD/MM/YYYY HH:mm" (hora militar, 24h).
 *  - Si hay nombre de negocio o punto de caja, los antepone para identificación.
 *
 * QUÉ EXPORTA: buildCierreShareCaption (named), buildCierreFilename (named)
 *
 * QUÉ NO HACE:
 *  - No genera el reporte completo en texto (eso era el patrón anterior y se
 *    eliminó porque el destinatario podía editarlo y falsear el reporte).
 *  - No conoce de UI ni de backend.
 *
 * REGLAS:
 *  - El compartir SIEMPRE debe ir acompañado de la IMAGEN del ticket (no editable).
 *  - El caption es solo para identificar el archivo en el chat / email.
 *  - Hora siempre en 24h: "HH:mm" — evita confusiones AM/PM en horarios nocturnos.
 */

import { formatDate, formatTime } from "@/lib/utils/formatters";
import { format } from "date-fns";
import { es } from "date-fns/locale";

/** Devuelve la fecha de referencia del cierre: cerradaEn si existe, si no abiertaEn. */
function getCierreDate(cierre: {
  abiertaEn: string;
  cerradaEn?: string | null;
}): Date {
  return new Date(cierre.cerradaEn || cierre.abiertaEn);
}

export type CierreShareDocumentKind = "cierre" | "parcial" | "jornada";

interface CierreCaptionInput {
  abiertaEn: string;
  cerradaEn?: string | null;
  puntoCajaNombre?: string;
  label?: string;
}

function filenameSegment(value: string, fallback: string): string {
  const segment = value
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\p{L}\p{N}._-]+/gu, "_")
    .replace(/^_+|_+$/g, "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.replace(/^./u, (letter) => letter.toUpperCase()))
    .join("_");
  return segment || fallback;
}

function formatRegionalFilenameStamp(date: Date): string {
  const day = format(date, "EEE", { locale: es })
    .replace(/\./g, "")
    .replace(/^./, (letter) => letter.toUpperCase());
  const month = format(date, "MMM", { locale: es })
    .replace(/\./g, "")
    .replace(/^./, (letter) => letter.toUpperCase());
  const hour = format(date, "hh-mm");
  const meridiem = date.getHours() >= 12 ? "P.M." : "A.M.";
  return `${day}_${format(date, "dd")}_de_${month}_${format(date, "yyyy")}_${hour}_${meridiem}`;
}

/**
 * "Cierre de Caja • Caja principal • Turno mañana • 10/05/2026 23:13"
 * "Reporte Parcial de Caja • ALTENOT • Caja principal • 10/05/2026 23:13"
 */
export function buildCierreShareCaption(
  cierre: CierreCaptionInput,
  businessName?: string,
  kind: CierreShareDocumentKind = "cierre",
): string {
  const date = getCierreDate(cierre);
  const dateStr = `${formatDate(date, "P")} ${formatTime(date)}`;
  const parts: string[] =
    kind === "parcial"
      ? ["Reporte Parcial de Caja"]
      : kind === "jornada"
        ? ["Resumen Global de Jornada"]
        : ["Cierre de Caja"];
  if (businessName) parts.push(businessName);
  const labelFlat = cierre.label?.replace(/\s+/g, " ").trim();
  if (kind === "jornada" && labelFlat) {
    parts.push(labelFlat);
  } else if (cierre.puntoCajaNombre) {
    parts.push(cierre.puntoCajaNombre);
  }
  if (kind === "cierre" && labelFlat) parts.push(labelFlat);
  parts.push(dateStr);
  return parts.join(" • ");
}

/**
 * Nombre de archivo seguro para PNG/PDF de un cierre o reporte parcial.
 */
export function buildCierreFilename(
  cierre: CierreCaptionInput,
  kind: CierreShareDocumentKind = "cierre",
): string {
  const date = getCierreDate(cierre);
  const puntoConPrefijo = filenameSegment(
    cierre.puntoCajaNombre || "Caja",
    "Caja",
  );
  const punto = puntoConPrefijo.replace(/^Caja_(?=.)/iu, "");
  const turno = filenameSegment(cierre.label || "Turno", "Turno");
  const dateLabel = formatRegionalFilenameStamp(date);
  if (kind === "parcial")
    return `Reporte_Parcial_Caja_${punto}_${turno}_${dateLabel}`;
  if (kind === "jornada") {
    // Fecha sin hora: «Vie_21_de_Ago_2026» (primeras 5 partes del stamp regional).
    const fechaSola = dateLabel.split("_").slice(0, 5).join("_");
    return `Resumen_Jornada_${fechaSola}`;
  }
  return `Cierre_Caja_${punto}_${turno}_${dateLabel}`;
}
